import type { SessionContext } from '@esign/contracts'
import { stepFromLocation } from './route.js'

/**
 * Snapshot response từ partner/test-session dùng cho local demo.
 * Các bearer/refresh/access token không được ghi plaintext vào source.
 */
export const DEMO_PARTNER_SESSION = {
  session_id: 'ekyc_TzVPZs7eSY9SHOfNlBMM',
  webview_url: 'https://ekyc-middleware.test/webview/ekyc_TzVPZs7eSY9SHOfNlBMM',
  access_token_expires_at: '2026-08-11T07:40:04+00:00',
  expires_in: 900,
  refresh_token_expires_at: '2026-08-18T07:25:04+00:00',
  session_expired_at: '2026-08-11T07:55:04+00:00',
  identity_info: {
    email: 'user001@test.com',
    full_name: 'Nguyễn Văn A',
  },
} as const

export const DEMO_SESSION_ID = DEMO_PARTNER_SESSION.session_id

/**
 * Backend dev chưa cung cấp endpoint SessionContext cho signing-web, nên local
 * dùng snapshot tối thiểu này để render phiên mẫu. Production vẫn tải context
 * từ backend như bình thường.
 */
export const DEMO_SESSION_CONTEXT: SessionContext = {
  session: {
    id: DEMO_SESSION_ID,
    expires_at: DEMO_PARTNER_SESSION.session_expired_at,
    current_step: 'info',
    completed_steps: [],
    status: 'pending',
  },
  partner: {
    id: 'AT',
    name: 'AccessTrade',
    profile: 'non_bank',
  },
  container: {
    type: 'web',
    chrome: 'full',
    back: 'app_handled',
    exit: {
      type: 'redirect',
      target: 'https://accesstrade.vn',
      fallback_text: 'Quay lại AccessTrade',
    },
  },
  capabilities: {
    camera: true,
    file_upload: true,
    download: true,
  },
  flow: {
    id: 'at-partner-demo',
    steps: [
      {
        type: 'info',
        label: 'Thông tin',
        config: {
          fields: ['email'],
          editable: [],
          security_note: 'Thông tin được cung cấp từ phiên xác thực của đối tác.',
        },
      },
      {
        type: 'ekyc',
        label: 'Xác thực danh tính',
        config: {
          provider: 'FPT eKYC SDK',
          webview_url: 'https://demo.econtract.fpt.com/ekyc/sdk-kyc-v2',
          document_sides: 2,
          require_selfie: true,
        },
      },
    ],
  },
  theme: {
    brand_color: '#EF6820',
    secondary_color: '#EE46BC',
    on_primary: '#FFFFFF',
    radius: '12px',
    logo_id: null,
    mode: 'light',
    font: 'system',
  },
  legal: {
    template_id: 'AT-DEMO-CONTRACT',
    template_version: 1,
  },
  subject: {
    email: DEMO_PARTNER_SESSION.identity_info.email,
    full_name: DEMO_PARTNER_SESSION.identity_info.full_name,
    id_no: '079201012345',
    dob: '1990-01-01',
    id_images: ['demo-front.jpg', 'demo-back.jpg'],
  },
  i18n: { locale: 'vi' },
}

const DEMO_SIGNED_KEY = 'esign-demo-signed'
const DEMO_STEP_TYPES = ['info', 'ekyc'] as const

export function demoSessionContext(pathname: string, sessionId?: string): SessionContext {
  const match = pathname.match(/\/s\/([^/]+)\/([^/]+)/)
  const activeSessionId = sessionId || match?.[1] || DEMO_SESSION_ID
  const requested = stepFromLocation(pathname) || 'info'
  const signed =
    requested === 'signed' ||
    (typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem(`${DEMO_SIGNED_KEY}_${activeSessionId}`) === '1')
  const currentStep =
    !signed && (DEMO_STEP_TYPES as readonly string[]).includes(requested)
      ? (requested as SessionContext['session']['current_step'])
      : signed
        ? 'ekyc'
        : 'info'
  const currentIndex = DEMO_STEP_TYPES.indexOf(currentStep as any)

  const savedSdkUrl = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('dev_ekyc_sdk_url') : null
  const effectiveSdkUrl = savedSdkUrl || 'https://demo.econtract.fpt.com/ekyc/sdk-kyc-v2'

  const updatedSteps = DEMO_SESSION_CONTEXT.flow.steps.map((s) => {
    if (s.type === 'ekyc') {
      return {
        ...s,
        config: {
          ...s.config,
          provider: savedSdkUrl ? 'FPT EKYC SDK' : s.config.provider,
          webview_url: effectiveSdkUrl,
        },
      }
    }
    return s
  })

  const isRealSession = activeSessionId !== DEMO_SESSION_ID
  const savedIdentityStr =
    typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(`dev_identity_data_${activeSessionId}`) ||
        (!isRealSession ? sessionStorage.getItem('dev_identity_data') : null)
      : null
  let savedIdentity: Record<string, any> = {}
  if (savedIdentityStr) {
    try {
      savedIdentity = JSON.parse(savedIdentityStr)
    } catch {
      // ignore
    }
  }

  const subjectData = {
    ...(isRealSession
      ? {
          email: '',
          full_name: '',
          id_no: '',
          dob: '',
          phone: '',
          address: '',
          id_issued_date: '',
          id_issued_place: '',
          id_images: [],
        }
      : DEMO_SESSION_CONTEXT.subject),
    ...savedIdentity,
    id_no:
      savedIdentity.id_no ||
      savedIdentity.id_number ||
      savedIdentity.identification_number ||
      savedIdentity.soGTo ||
      (isRealSession ? '' : DEMO_SESSION_CONTEXT.subject.id_no),
    dob:
      savedIdentity.dob ||
      savedIdentity.date_of_birth ||
      savedIdentity.ngaySinh ||
      (isRealSession ? '' : DEMO_SESSION_CONTEXT.subject.dob),
    full_name:
      savedIdentity.full_name ||
      savedIdentity.name ||
      savedIdentity.hoTen ||
      (isRealSession ? '' : DEMO_SESSION_CONTEXT.subject.full_name),
    email: savedIdentity.email || (isRealSession ? '' : DEMO_SESSION_CONTEXT.subject.email),
    phone: savedIdentity.phone || savedIdentity.phone_number || (isRealSession ? '' : DEMO_SESSION_CONTEXT.subject.phone || ''),
    address:
      typeof savedIdentity.address === 'object'
        ? JSON.stringify(savedIdentity.address)
        : (savedIdentity.address || savedIdentity.diaChi || (isRealSession ? '' : DEMO_SESSION_CONTEXT.subject.address)),
    id_issued_date:
      savedIdentity.id_issued_date ||
      savedIdentity.issue_date ||
      savedIdentity.ngayCap ||
      (isRealSession ? '' : DEMO_SESSION_CONTEXT.subject.id_issued_date || ''),
    id_issued_place:
      savedIdentity.id_issued_place ||
      savedIdentity.issue_place ||
      savedIdentity.noiCap ||
      (isRealSession ? '' : DEMO_SESSION_CONTEXT.subject.id_issued_place || ''),
    front_image_url:
      savedIdentity.front_image_url ||
      savedIdentity.front_image ||
      savedIdentity.frontSide,
    back_image_url:
      savedIdentity.back_image_url ||
      savedIdentity.back_image ||
      savedIdentity.backSide,
  }

  return {
    ...DEMO_SESSION_CONTEXT,
    session: {
      ...DEMO_SESSION_CONTEXT.session,
      id: activeSessionId,
      current_step: currentStep,
      status: signed ? 'signed' : 'pending',
      completed_steps: signed ? [...DEMO_STEP_TYPES.slice(0, -1)] : DEMO_STEP_TYPES.slice(0, currentIndex),
    },
    flow: {
      ...DEMO_SESSION_CONTEXT.flow,
      steps: updatedSteps,
    },
    subject: subjectData,
  }
}

export function markDemoSigned(sessionId?: string): void {
  const id = sessionId || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('dev_session_id') : null) || DEMO_SESSION_ID
  sessionStorage.setItem(`${DEMO_SIGNED_KEY}_${id}`, '1')
  sessionStorage.setItem(`esign_just_signed_${id}`, '1')
}
